import { useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { twMerge } from 'tailwind-merge';
import {
  formatYearReferenceLabel,
  getPersonLabel,
  type EventYearReferenceInput,
  type EventYearReferencePerson,
  type GoalYearReference,
  type YearReferencePickerValue,
} from '../lib/life-events';
import { BottomSheet } from './BottomSheet';
import { TextInput } from './TextInput';

export type { YearReferencePickerValue } from '../lib/life-events';

interface YearReferencePickerProps {
  label: string;
  value: YearReferencePickerValue;
  onChange: (next: YearReferencePickerValue) => void;
  profiles: EventYearReferencePerson[];
  currentFireYear: number | null | undefined;
  disabled?: boolean;
  calendarPlaceholder?: string;
  agePlaceholder?: string;
  onOpenChange?: (open: boolean) => void;
  testIDPrefix?: string;
}

interface YearReferenceOption {
  key: string;
  label: string;
  value: Partial<YearReferencePickerValue>;
}

interface YearReferenceSection {
  title: string;
  options: YearReferenceOption[];
}

function toReferenceInput(
  value: YearReferencePickerValue,
): EventYearReferenceInput {
  const parsedYear = Number.parseInt(value.year, 10);
  const parsedAge = Number.parseInt(value.age, 10);

  return {
    ref: value.ref,
    year: Number.isFinite(parsedYear) ? parsedYear : null,
    personId: value.personId || null,
    age: Number.isFinite(parsedAge) ? parsedAge : null,
  };
}

export function YearReferencePicker({
  label,
  value,
  onChange,
  profiles,
  currentFireYear,
  disabled = false,
  calendarPlaceholder = 'Year',
  agePlaceholder = 'Age',
  onOpenChange,
  testIDPrefix,
}: YearReferencePickerProps) {
  const [open, setOpen] = useState(false);

  const setPickerOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  };

  const sections = useMemo<YearReferenceSection[]>(() => {
    const personSections = profiles.map((profile) => ({
      title: getPersonLabel(profile),
      options: [
        {
          key: `${profile.id}-retirement`,
          label: 'Retirement',
          value: { ref: 'retirement' as const, personId: profile.id, age: '' },
        },
        {
          key: `${profile.id}-death`,
          label: 'Death',
          value: { ref: 'death' as const, personId: profile.id, age: '' },
        },
        {
          key: `${profile.id}-age`,
          label: 'When age…',
          value: { ref: 'age' as const, personId: profile.id },
        },
      ],
    }));

    return [
      {
        title: 'Calendar Year',
        options: [
          {
            key: 'calendar-year',
            label: 'Calendar Year',
            value: { ref: 'calendar_year', personId: '', age: '' },
          },
        ],
      },
      ...personSections,
      ...(profiles.length > 1
        ? [
            {
              title: 'Household',
              options: [
                {
                  key: 'first-death',
                  label: 'At First Death',
                  value: {
                    ref: 'first_death' as const,
                    personId: '',
                    age: '',
                  },
                },
              ],
            },
          ]
        : []),
    ];
  }, [profiles]);

  const currentLabel = formatYearReferenceLabel(
    toReferenceInput(value),
    profiles,
    currentFireYear,
  );
  const triggerLabel =
    value.ref === 'calendar_year' ? 'Calendar Year' : currentLabel;

  const handleSelect = (patch: Partial<YearReferencePickerValue>) => {
    onChange({ ...value, ...patch });
    setPickerOpen(false);
  };

  return (
    <View>
      <Text className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
        {label}
      </Text>
      <Pressable
        testID={testIDPrefix ? `${testIDPrefix}-picker` : undefined}
        accessibilityLabel={testIDPrefix ? `${label} reference picker` : undefined}
        onPress={() => {
          if (!disabled) setPickerOpen(true);
        }}
        className={twMerge(
          'flex-row items-center justify-between rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 px-3 py-2',
          disabled && 'opacity-50',
        )}
      >
        <Text className="flex-1 text-sm text-gray-800 dark:text-gray-100">
          {triggerLabel}
        </Text>
        <Text className="text-xs font-semibold text-gray-400 dark:text-gray-500 ml-2">
          ▼
        </Text>
      </Pressable>

      {value.ref === 'calendar_year' && (
        <TextInput
          testID={testIDPrefix ? `${testIDPrefix}-input` : undefined}
          accessibilityLabel={testIDPrefix ? `${label} calendar year` : undefined}
          value={value.year}
          onChangeText={(year) => onChange({ ...value, year })}
          placeholder={calendarPlaceholder}
          keyboardType="numeric"
          editable={!disabled}
          size="sm"
          containerClassName="mt-2"
        />
      )}

      {value.ref === 'age' && (
        <TextInput
          testID={testIDPrefix ? `${testIDPrefix}-age-input` : undefined}
          accessibilityLabel={testIDPrefix ? `${label} age` : undefined}
          value={value.age}
          onChangeText={(age) => onChange({ ...value, age })}
          placeholder={agePlaceholder}
          keyboardType="numeric"
          editable={!disabled}
          size="sm"
          containerClassName="mt-2"
        />
      )}

      <BottomSheet
        visible={open}
        onClose={() => setPickerOpen(false)}
        title={`Select ${label}`}
        variant="centered"
      >
        <ScrollView className="p-4" contentContainerClassName="gap-4">
          {sections.map((section) => (
            <View key={section.title} className="gap-2">
              <Text className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {section.title}
              </Text>
              {section.options.map((option) => {
                const optionValue = { ...value, ...option.value };
                const selected =
                  value.ref === optionValue.ref &&
                  value.personId === optionValue.personId;

                return (
                  <Pressable
                    testID={testIDPrefix ? `${testIDPrefix}-option-${option.key}` : undefined}
                    accessibilityLabel={testIDPrefix ? `${label} option ${option.label}` : undefined}
                    key={option.key}
                    onPress={() => handleSelect(option.value)}
                    className={twMerge(
                      'rounded-xl border px-3.5 py-3 bg-white dark:bg-gray-800',
                      selected
                        ? 'border-green-600 dark:border-green-500 bg-green-50 dark:bg-green-900/30'
                        : 'border-gray-200 dark:border-gray-600',
                    )}
                  >
                    <Text
                      className={twMerge(
                        'text-sm',
                        selected
                          ? 'text-green-700 dark:text-green-400 font-semibold'
                          : 'text-gray-800 dark:text-gray-200',
                      )}
                    >
                      {option.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          ))}
        </ScrollView>
      </BottomSheet>
    </View>
  );
}
